import { getSession } from 'next-auth/react';

// Server-side: INTERNAL_API_BASE_URL 직접 호출 (CF 회로 우회)
// Client-side: NEXT_PUBLIC_API_BASE_URL: 빈 문자열이면 same-origin
export const API_BASE_URL =
  typeof window === 'undefined'
    ? process.env.INTERNAL_API_BASE_URL || 'http://localhost:8080'
    : process.env.NEXT_PUBLIC_API_BASE_URL ?? '';

export interface FetchOptions extends RequestInit {
  token?: string;
  params?: Record<string, string>;
  /** 401 에러 시 세션 갱신 후 재시도 여부 (기본: true) */
  retryOnUnauthorized?: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** 인증 에러 여부 */
  isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** 권한 에러 여부 */
  isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * 현재 세션에서 유효한 토큰을 가져옴
 * 토큰이 만료되었거나 에러가 있으면 세션 갱신을 시도
 */
async function getValidToken(): Promise<string | null> {
  const session = await getSession();
  if (!session) return null;

  // 세션에 에러가 있으면 (refresh 실패 등) null 반환
  if (session.error) {
    console.warn('[API] 세션에 에러 존재:', session.error);
    return null;
  }

  return session.accessToken || null;
}

/**
 * 기본 API 호출 함수
 * 토큰을 직접 전달하는 방식
 */
export async function fetchAPI<T = unknown>(
  endpoint: string,
  options: FetchOptions = {},
): Promise<T> {
  const { token, params, retryOnUnauthorized = true, ...fetchOptions } = options;

  let url = `${API_BASE_URL}${endpoint}`;

  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  const isFormDataBody =
    typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
  const headers: Record<string, string> = {};

  if (!isFormDataBody) {
    headers['Content-Type'] = 'application/json';
  }

  if (fetchOptions.headers) {
    Object.assign(headers, fetchOptions.headers);
  }

  if (typeof window !== 'undefined') {
    const qaTraceId = window.sessionStorage.getItem('hai.qa.traceId');
    if (qaTraceId) {
      headers['X-QA-Trace-Id'] = qaTraceId;
    }
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  // 401 에러 시 세션 갱신 후 재시도
  if (response.status === 401 && retryOnUnauthorized && token) {
    console.warn('[API] 401 Unauthorized - 세션 갱신 시도');

    try {
      const freshToken = await getValidToken();

      if (freshToken && freshToken !== token) {
        console.log('[API] 새 토큰으로 재시도');
        return fetchAPI<T>(endpoint, {
          ...options,
          token: freshToken,
          retryOnUnauthorized: false,
        });
      }

      // 토큰 갱신 실패 또는 같은 토큰 반환 → 세션 만료로 처리
      console.warn('[API] 토큰 갱신 실패 - 세션 만료 처리');
      throw new ApiError('세션이 만료되었습니다. 다시 로그인해주세요.', 401, {
        error: 'SessionExpired',
      });
    } catch (refreshError) {
      if (refreshError instanceof ApiError) throw refreshError;
      console.error('[API] 세션 갱신 실패:', refreshError);
      throw new ApiError('세션이 만료되었습니다. 다시 로그인해주세요.', 401, {
        error: 'SessionExpired',
      });
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new ApiError(
      errorData?.message || `API Error: ${response.status}`,
      response.status,
      errorData,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Poll 토큰 기반 API 호출 함수 (Guest 엔드포인트용)
 * Authorization: Poll ${pollToken} 헤더를 자동 설정
 * FormData body 시 Content-Type 자동 생략
 */
export async function fetchPollAPI<T = unknown>(
  endpoint: string,
  pollToken: string,
  options: Omit<FetchOptions, 'token' | 'retryOnUnauthorized'> = {},
): Promise<T> {
  const { params, ...fetchOptions } = options;

  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    url += `?${new URLSearchParams(params).toString()}`;
  }

  const headers: Record<string, string> = {
    Authorization: `Poll ${pollToken}`,
  };

  // FormData 는 Content-Type 을 브라우저가 자동 설정
  if (fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (fetchOptions.headers) {
    Object.assign(headers, fetchOptions.headers);
  }

  const response = await fetch(url, { ...fetchOptions, headers });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new ApiError(
      errorData?.message || `API Error: ${response.status}`,
      response.status,
      errorData,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * 세션 기반 API 호출 함수
 * 자동으로 현재 세션의 토큰을 사용
 * 클라이언트 컴포넌트에서 사용
 */
export async function fetchWithSession<T = unknown>(
  endpoint: string,
  options: Omit<FetchOptions, 'token'> = {},
): Promise<T> {
  const session = await getSession();
  
  if (!session?.accessToken) {
    throw new ApiError('인증이 필요합니다', 401);
  }

  // 세션 에러 체크
  if (session.error) {
    console.warn('[API] 세션 에러 감지:', session.error);
    throw new ApiError('세션이 만료되었습니다', 401, { error: session.error });
  }

  return fetchAPI<T>(endpoint, {
    ...options,
    token: session.accessToken,
  });
}

/**
 * 파일 내려받기용 세션 인증 호출. 응답을 파싱하지 않고 그대로 돌려준다.
 *
 * `fetchWithSession` 은 JSON 을 파싱해 값을 주므로 Blob 을 받을 수 없다. 그래서
 * 다운로드 경로들이 화면이 들고 있는 `session.accessToken`(React state 스냅샷)을
 * 직접 써 왔는데, 그 값은 **스스로 갱신하지 못한다**. `useSession` 의 갱신은 토큰
 * 만료 시점과 동기화되지 않으므로 "만료됐는데 아직 갱신 전" 인 간극이 열리고,
 * 그 창에 걸린 내려받기만 401 로 죽는다 — 표와 처리완료는 멀쩡한데 CSV·PDF 만
 * 실패하는 비대칭이 여기서 나왔다 (2026-08-20 담당자 라운드 §2.2).
 *
 * 우회는 갱신뿐 아니라 `fetchAPI` 의 **401 재시도** 도 함께 잃게 한다. 이 함수는
 * 호출마다 세션을 새로 읽고, 그래도 401 이면 갱신된 토큰으로 한 번 재시도한다.
 */
export async function fetchBlobWithSession(
  endpoint: string,
  options: Omit<FetchOptions, 'token'> = {},
): Promise<Response> {
  const { params, retryOnUnauthorized = true, ...fetchOptions } = options;

  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    url += `?${new URLSearchParams(params).toString()}`;
  }

  const token = await getValidToken();
  if (!token) {
    throw new ApiError('인증이 필요합니다', 401);
  }

  const send = (bearer: string): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (fetchOptions.headers) {
      Object.assign(headers, fetchOptions.headers);
    }
    if (typeof window !== 'undefined') {
      const qaTraceId = window.sessionStorage.getItem('hai.qa.traceId');
      if (qaTraceId) {
        headers['X-QA-Trace-Id'] = qaTraceId;
      }
    }
    headers['Authorization'] = `Bearer ${bearer}`;
    return fetch(url, { ...fetchOptions, headers });
  };

  const response = await send(token);
  if (response.status !== 401 || !retryOnUnauthorized) {
    return response;
  }

  console.warn('[API] 내려받기 401 - 세션 갱신 시도');
  const freshToken = await getValidToken();
  if (!freshToken || freshToken === token) {
    return response;
  }
  return send(freshToken);
}
